import { CheckCircle2, Circle, Clock, Info } from "lucide-react";
import type { IdvTone } from "@/lib/applicantIdvState";

/**
 * BF-B5 — the operator-facing IDV chip. Shared by the case review panel and the
 * dashboard queue so the two cannot disagree about a case, which is the same
 * reason the backend derives the state in one place (platform#226).
 *
 * `attention` is amber, never red. Neither an opt-out nor an incomplete check is
 * an adverse finding (ADR-012 R3, company constitution Core Value 1), and red is
 * the colour this product reserves for genuine inconsistency — see StatusBadge's
 * `inconsistent`. Amber says "look at this", which is exactly the ask.
 */

const TONE_STYLES: Record<IdvTone, string> = {
  verified: "bg-trust-green-muted text-trust-green border border-trust-green/20",
  attention: "bg-trust-amber-muted text-trust-amber-foreground border border-trust-amber/20",
  progress: "bg-trust-blue-muted text-trust-blue border border-trust-blue/20",
  neutral: "bg-muted text-muted-foreground border border-border",
};

const TONE_ICONS: Record<IdvTone, typeof CheckCircle2> = {
  verified: CheckCircle2,
  attention: Info,
  progress: Clock,
  neutral: Circle,
};

export interface ApplicantIDVStateBadgeProps {
  label: string;
  tone: IdvTone;
  /** Compact drops the icon and tightens padding, for the dashboard queue. */
  compact?: boolean;
  /** Native tooltip, used in the queue where the detail line has nowhere to go. */
  title?: string;
}

export function ApplicantIDVStateBadge({
  label,
  tone,
  compact = false,
  title,
}: ApplicantIDVStateBadgeProps) {
  const Icon = TONE_ICONS[tone];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ${
        compact ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"
      } ${TONE_STYLES[tone]}`}
      title={title}
    >
      {!compact && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
      {label}
    </span>
  );
}
